// The badge a machine wears: a foundry saving for a recipe, an oven with
// something in it, a piece working a heap — and the king, which is all three
// and may only be one of them a turn.

import {
  type Color,
  KING,
  type PieceType,
  pieceType,
  squareName
} from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { pieceAt } from "src/engine/rules/position";
import {
  canForge,
  isFoundryAt,
  isHarvesterAt,
  isRefinery,
  kingCapacity,
  kingTask
} from "src/engine/rules/industry/machines";
import {
  bufferAtLeast,
  bufferTotal,
  cookingAt,
  cookingCount,
  handoffAt,
  mineableAt,
  kingTaskAt,
  minedAtOn,
  mintFacingAt,
  targetAt,
  unitGrade,
  dumpsAt
} from "src/engine/rules/industry/state";
import { pieceLabel } from "src/engine/rules/naming";
import {
  HARVEST_RATE,
  GRADE_NAMES, PIECE_NAMES, mintCost } from "src/engine/rules/rules";
import { METAL, mintGradeOf } from "src/engine/rules/materials";
import type { BadgeCore, PieceBadge } from "./types";
import { buildOptions, forgeOptions } from "./options";

export function foundryBadge(state: GameState, sq: number, owner: Color): BadgeCore {
  const target = targetAt(state.industry, sq) as PieceType | 0;
  const held = bufferTotal(state.industry, sq, "in");
  const recipes = forgeOptions(state, sq, owner);
  const affordable = recipes.filter((option) => option.enabled).length;
  const type = pieceType(pieceAt(state.position, sq));
  const name = pieceLabel(state.position, sq);
  // Every foundry gets the switch. A rook stockpiles by default — it is
  // saving for a recipe — but "default" is a default rather than a law, and a
  // foundry sitting on material it can no longer use was a dead end when the
  // control was not offered at all.
  const routes = true;
  const also = otherRoles(state, sq);

  if (!target) {
    return {
      square: sq,
      owner,
      kind: "foundry",
      // How many recipes it can pay for, else what it holds, else nothing.
      // It used to read "- / ?" — a prompt to choose a recipe — but since the
      // hotbar forges (U4) nothing sets one, so the prompt sat on every rook
      // and king for good.
      short: affordable > 0 ? `⚒ ${affordable}` : held > 0 ? `${held}` : "",
      title: `${name} · ${squareName(sq)}`,
      // The hotbar forges (U4); what is left to say here is the machine's own
      // rule, and the inspector keeps it behind a hover.
      detail:
        `Forges from the hotbar onto a square beside it — only on a turn you have not moved, and once you forge you cannot move (Factory Flow). ` +
        `Material counts down one for one, so anything refined past what a ` +
        `recipe asks for still pays for it.` +
        (isRefinery(type) ? ` This piece ${also}, but only one job a turn.` : ""),
      target: 0,
      recipes,
      builds: buildOptions(state, sq, owner),
      routes,
      // **The facing dial is offered on `recipes`, so it has to be answered
      // here too.** This branch is a foundry with no recipe chosen — which is
      // every king by default and every rook until you pick one — and the
      // dial sits *above* the recipe list, so "point the new pawns, then
      // choose what to build" is the natural order and was the one order in
      // which it did not work. It rendered, it dispatched, the reducer stored
      // the facing, and the badge never read it back: the pip you clicked
      // stayed dark and the dial said "auto" forever. A control that changes
      // state and shows nothing is indistinguishable from a dead one.
      mintFacing: mintFacingAt(state.industry, sq),
      needsSetup: affordable > 0,
      dumps: dumpsAt(state.industry, sq),
      ...kingDuty(state, sq, owner)
    };
  }

  const need = mintCost(target);
  const grade = mintGradeOf(target);
  const usable = bufferAtLeast(state.industry, sq, grade, "in", METAL);
  const ready = usable >= need;

  return {
    square: sq,
    owner,
    kind: "foundry",
    short: `${usable}/${need}`,
    title: `${name} · saving for a ${PIECE_NAMES[target]}`,
    detail:
      `Needs ${need} units of ${GRADE_NAMES[grade]} or better, and has ${usable}. ` +
      (ready
        ? `That is enough — forge it from the hotbar, before you move this turn.`
        : `${need - usable} to go. Anything below ${GRADE_NAMES[grade]} does ` +
          `not count toward this, so it needs refining first. Anything above ` +
          `does, one for one.`),
    progress: Math.min(1, usable / need),
    ready,
    target,
    recipes,
    builds: buildOptions(state, sq, owner),
    routes,
    mintFacing: mintFacingAt(state.industry, sq),
    dumps: dumpsAt(state.industry, sq),
    ...kingDuty(state, sq, owner)
  };
}

export function refineryBadge(state: GameState, sq: number, owner: Color): BadgeCore | null {
  const inOven = cookingCount(state.industry, sq);
  const cooking = inOven > 0;
  const inBank = bufferTotal(state.industry, sq, "in");
  const outBank = bufferTotal(state.industry, sq, "out");
  if (!cooking && inBank === 0 && outBank === 0) return null;

  const grade = cooking ? unitGrade(cookingAt(state.industry, sq)) : -1;

  return {
    square: sq,
    owner,
    kind: "refinery",
    short: cooking ? `→${grade}${inOven > 1 ? `×${inOven}` : ""}` : `${inBank + outBank}`,
    title: `Refinery · ${squareName(sq)}`,
    detail: cooking
      ? `Cooking one unit up to ${GRADE_NAMES[grade]}; it comes out at the end ` +
        `of your turn. Refineries raise one unit one grade per turn, and will ` +
        `never re-process material they have already handled — reaching the ` +
        `top grade takes three different machines.`
      : `Holding ${inBank} in and ${outBank} out, with nothing in the oven. ` +
        `It takes material from any belt of yours touching it, or by hand from ` +
        `a pawn standing next to it.`,
    ready: cooking,
    routes: true,
    dumps: dumpsAt(state.industry, sq),
    handoff: handoffAt(state.industry, sq),
    ...kingDuty(state, sq, owner)
  };
}

/**
 * What else this piece could be doing, for a piece that wears more than one
 * hat. The king is the whole reason this exists: it harvests, refines and
 * forges, and the badge could only ever describe one of them at a time — so a
 * king standing on a heap looked like a miner and nothing else, and the player
 * had no way to know its oven was idle *because* it was mining.
 */
function otherRoles(state: GameState, sq: number): string {
  const piece = pieceAt(state.position, sq);
  const type = pieceType(piece);
  const roles: string[] = [];
  if (isHarvesterAt(state.position, sq)) roles.push("harvests");
  if (isRefinery(type)) roles.push("refines");
  if (isFoundryAt(state.position, state.industry, sq)) roles.push("forges");
  return roles.join(", ");
}

/**
 * The king's job, tempo and pace, folded onto whatever badge it is wearing.
 *
 * A king is a miner *and* an oven *and* a foundry, and it does exactly one of
 * them a turn — so the one thing its badge has to answer is "which, and how
 * fast", and no badge kind on its own can say that.
 */
function kingDuty(state: GameState, sq: number, owner: Color): Partial<PieceBadge> {
  if (pieceType(pieceAt(state.position, sq)) !== KING) return {};
  return {
    kingTask: kingTaskAt(state.industry, sq),
    kingDoing: kingTask(state.position, state.industry, sq, owner),
    kingTempo: kingCapacity(state.industry, sq)
  };
}

export function minerBadge(state: GameState, sq: number, owner: Color): BadgeCore {
  const left = mineableAt(state.industry, sq);
  const spent = minedAtOn(state.industry, sq) === state.industry.turnCounter;
  const type = pieceType(pieceAt(state.position, sq));
  const rate = HARVEST_RATE[type];
  const roles = otherRoles(state, sq);

  return {
    square: sq,
    owner,
    kind: "miner",
    short: `${left}`,
    title: `${pieceLabel(state.position, sq)} working a heap · ${left} left`,
    detail:
      `Digs ${rate} a turn` +
      (spent ? ", and has already dug this turn. " : ". ") +
      `It is free to walk away and come back — the heap keeps what is left. ` +
      `Hand the unit to a machine it is touching, or onto one of your belts.` +
      // The king wears every hat and may only wear one at a time, so its
      // badge has to name the others and say why they are idle.
      (roles.includes("refines")
        ? ` This piece ${roles} as well, but only one job a turn — and every ` +
          `turn it is left on the same one it gets through more of it.`
        : ""),
    progress: spent ? 1 : 0,
    routes: true,
    dumps: dumpsAt(state.industry, sq),
    handoff: handoffAt(state.industry, sq),
    ...kingDuty(state, sq, owner)
  };
}

/**
 * Why a foundry that can afford its recipe has not spent it.
 *
 * Every one of these is a *durable* state except the last, which is why the
 * sentence matters: a foundry sitting at 6/2 looks identical whether it is
 * about to build on its own turn or has been unable to build for forty of
 * them.
 */
export function forgeBlocker(
  state: GameState,
  sq: number,
  owner: Color,
  target: PieceType
): string {
  switch (canForge(state.position, state.industry, sq, owner, target)) {
    case "no-room":
      return "no empty square beside it to put one on";
    // A king does one job a turn — mine, refine or forge — and it has already
    // spent this one. Clears on its own next turn.
    case "king-busy":
      return "this king has already worked this turn";
    case "not-mintable":
      return "this army cannot make one";
    case "too-poor":
      // Not a contradiction with the count beside it: the count is what the
      // foundry holds at the grade, not what the bill asks.
      return "the bill is more than it is holding";
    case "not-your-foundry":
      return "not a foundry any more";
    case "no-foundry":
      return "nothing of yours touches the square it would go on";
    case "no-coalition":
      return "the foundries beside that square cannot pay for it between them";
    default:
      return "ready to forge";
  }
}
